import React, { useState } from 'react'
import { useAuthStore } from '../store/authStore'
import { FiMail, FiUser, FiPhone, FiBriefcase, FiSave, FiAlertCircle, FiCheck } from 'react-icons/fi'
import axios from '../config/axios'

function ProfilePage() {
  const { user, login } = useAuthStore()

  const [firstName, setFirstName] = useState(user?.displayName?.split(' ')[0] || '')
  const [lastName, setLastName] = useState(user?.displayName?.split(' ').slice(1).join(' ') || '')
  const [companyName, setCompanyName] = useState(user?.companyName || '')
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess(false)
    if (!firstName || !lastName || !companyName) {
      setError('Please fill in all fields')
      return
    }
    setSaving(true)
    try {
      const { updateProfile } = await import('firebase/auth')
      const { auth, db } = await import('../config/firebase')
      const { doc, updateDoc } = await import('firebase/firestore')

      const fullName = `${firstName} ${lastName}`.trim()
      await updateProfile(auth.currentUser, { displayName: fullName })

      await updateDoc(doc(db, 'users', user.uid), { firstName, lastName, fullName, companyName })

      // Update local store
      const { useAuthStore: store } = await import('../store/authStore')
      store.setState(s => ({ user: { ...s.user, displayName: fullName, companyName } }))

      // Build changes list for notification
      const changes = []
      if (fullName !== user.displayName) changes.push({ field: 'Name', from: user.displayName, to: fullName })
      if (companyName !== user.companyName) changes.push({ field: 'Company', from: user.companyName, to: companyName })
      if (changes.length > 0) {
        axios.post('/api/auth/notify-profile-update', {
          email: user.email, firstName, lastName, companyName, cellNumber: user.cellNumber, changes
        }).catch(() => {})
      }

      setSuccess(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="card">
        <h2 className="text-2xl font-bold text-accent-yellow mb-6">Profile Settings</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-800 rounded flex gap-2 text-red-200 text-sm">
            <FiAlertCircle className="flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-teal-900/20 border border-teal-700 rounded flex gap-2 text-teal-300 text-sm">
            <FiCheck className="flex-shrink-0 mt-0.5" />
            Profile updated successfully.
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="flex items-center text-accent-teal font-bold mb-2">
                <FiUser className="mr-2" /> First Name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className="input-field"
                disabled={saving}
              />
            </div>
            <div>
              <label className="flex items-center text-accent-teal font-bold mb-2">
                Last Name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className="input-field"
                disabled={saving}
              />
            </div>
          </div>

          <div>
            <label className="flex items-center text-accent-teal font-bold mb-2">
              <FiBriefcase className="mr-2" /> Company
            </label>
            <input
              type="text"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              className="input-field"
              disabled={saving}
            />
          </div>

          <div>
            <label className="flex items-center text-accent-teal font-bold mb-2">
              <FiMail className="mr-2" /> Email Address
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="input-field opacity-50 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Email cannot be changed — contact us if needed.</p>
          </div>

          <div>
            <label className="flex items-center text-accent-teal font-bold mb-2">
              <FiPhone className="mr-2" /> Cell Number
            </label>
            <input
              type="tel"
              value={user?.cellNumber || ''}
              disabled
              className="input-field opacity-50 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Cell number cannot be changed — contact us if needed.</p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full btn-primary mt-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSave size={15} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </main>
  )
}

export default ProfilePage
